import { APP_BASE_HREF } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { IdTokenClaims } from '@azure/msal-common';
import { InvalidTokenError, jwtDecode, JwtPayload } from 'jwt-decode';
import {
  Configuration,
  ForgotPasswordRequest,
  ImpersonateRequest,
  LoginRequest,
  ResetPasswordRequest,
  TokenResponseResult,
  TwoFactorVerificationRequest,
  UserXatSvc,
} from 'net-api-lib';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService, StorageSerializer } from 'ngx-localstorage';
import { ServiceOptions } from 'ngx-localstorage/lib/interfaces/service-options';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AjsFrameService } from '../../shared/ajs-frame/ajs-frame.service';
import { AutoUnsubscribe } from '../auto-unsubscribe';
import { b2cPolicies } from './auth-config';

export const AZURE_AUTH_API_ENDPOINT = new InjectionToken<string>(
  'AzureAuthApiEndpoint'
);

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

@AutoUnsubscribe()
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  subs = new SubSink();

  private passThruSerializer = new PassThruSerializer();
  private accessToken = new BehaviorSubject<string>('');
  public identityToken$ = new BehaviorSubject<string>('');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    @Inject(APP_BASE_HREF) private baseHref: string,
    private apiConfiguration: Configuration,
    private userService: UserXatSvc,
    private localStorageService: LocalStorageService,
    private cookieService: CookieService,
    private ajsFrameService: AjsFrameService,
    private httpClient: HttpClient,
    @Inject(MSAL_GUARD_CONFIG)
    private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(AZURE_AUTH_API_ENDPOINT)
    private azrueAuthApiEndpoint: string
  ) {
    this.subs.sink = this.accessToken.subscribe(s => {
      this.apiConfiguration.credentials['BearerSecurityScheme'] = s;
    });

    this.subs.sink = this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.subs.sink = this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            (msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            msg.interactionType === InteractionType.Redirect
        ),
        tap(result => {
          const payload = result.payload as AuthenticationResult;
          this.identityToken$.next(payload.idToken);
        })
      )
      .subscribe(() => {
        this.router.navigate(['/azure/loginCallback'], {
          queryParams: this.route.snapshot.queryParams,
        });
      });

    this.subs.sink = this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        tap(result => {
          const payload = result.payload as AuthenticationResult;
          const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

          if (
            idtoken.acr === b2cPolicies.names.signUpSignIn ||
            idtoken.tfp === b2cPolicies.names.signUpSignIn
          ) {
            this.msalAuthService.instance.setActiveAccount(payload.account);
          }
        })
      )
      .subscribe();
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalAuthService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalAuthService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  twoFactorVerification(
    model: TwoFactorVerificationRequest
  ): Observable<boolean> {
    return this.userService.twoFactorVerification(model).pipe(
      take(1),
      map(m => {
        if (m.isSuccess) {
          return m.content?.accessToken ?? '';
        }
        throw m;
      }),
      tap(token => this.accessToken.next(token)),
      tap(token => {
        this.localStorageService.set('jwtToken', token, {
          serializer: this.passThruSerializer,
        } as ServiceOptions);

        this.localStorageService.set('authMethod', 0, {
          serializer: this.passThruSerializer,
        } as ServiceOptions);
      }),
      map(() => true)
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.userService
      .forgotPassword({
        email: email.trim(),
      } as ForgotPasswordRequest)
      .pipe(
        take(1),
        map(m => {
          if (m.isSuccess) {
            return m.isSuccess;
          }
          throw m.errors;
        })
      );
  }

  resetPassword(model: ResetPasswordRequest): Observable<boolean> {
    return this.userService.resetPassword(model).pipe(
      take(1),
      map(m => {
        return m.isSuccess ?? false;
      })
    );
  }

  msalSignIn(
    userFlowRequest?: RedirectRequest | PopupRequest,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loginHint?: string
  ) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.subs.sink = this.msalAuthService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalAuthService.instance.setActiveAccount(response.account);
          });
      } else {
        this.subs.sink = this.msalAuthService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalAuthService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        // see https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso#sso-between-different-apps
        // we need more then just the loginHint, also a domain_hint which is a tid (tenant id) cause we are in a multi-tenant setup
        // if (loginHint) {
        //     (this.msalGuardConfig.authRequest as any)['loginHint'] =
        //         loginHint;
        //     (this.msalGuardConfig.authRequest as any)['domainHint'] =
        //         'dd8ab8be-88f4-4389-9751-d5ad5cd4dc98'; // is this just our azrue b2c ad or the clients ad?
        // }

        const tmp = {
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest;

        this.msalAuthService.loginRedirect(tmp);
      } else {
        this.msalAuthService.loginRedirect(userFlowRequest);
      }
    }
  }

  azureSignIn(): Observable<TokenResponseResult> {
    return this.userService.azureAdLogin().pipe(
      take(1),
      tap(result => {
        if (result.content?.accessToken) {
          this.accessToken.next(result.content?.accessToken);

          this.localStorageService.set(
            'jwtToken',
            result.content?.accessToken,
            {
              serializer: this.passThruSerializer,
            } as ServiceOptions
          );

          this.localStorageService.set('authMethod', 1, {
            serializer: this.passThruSerializer,
          } as ServiceOptions);
        }
      })
    );
  }

  signIn(username: string, password: string): Observable<TokenResponseResult> {
    return this.userService
      .login({ username: username.trim(), password } as LoginRequest)
      .pipe(
        take(1),
        tap(result => {
          if (result.content?.accessToken) {
            this.accessToken.next(result.content?.accessToken);

            this.localStorageService.set(
              'jwtToken',
              result.content?.accessToken,
              {
                serializer: this.passThruSerializer,
              } as ServiceOptions
            );

            this.localStorageService.set('authMethod', 0, {
              serializer: this.passThruSerializer,
            } as ServiceOptions);
          }
        })
      );
  }

  signOut(): Observable<boolean> {
    this.msalAuthService.instance.logout();

    this.localStorageService.remove('jwtToken');
    this.localStorageService.remove('authMethod');
    this.cookieService.delete('JSESSIONID', '/');

    this.accessToken.next('');

    this.ajsFrameService.navigate('/angularjs/login');

    // clear to prevent issues with simap when switching between users
    this.cookieService.deleteAll('/');
    window.sessionStorage.clear();

    // temporary hack; remove as soon as we can get rid of role-routing.module
    window.location.href = this.baseHref;

    return of(true);
  }

  isAuthenticated(): Observable<boolean> {
    return this.accessToken.pipe(map(m => !!m));
  }

  getAccessToken(): Observable<string> {
    return this.accessToken;
  }

  refreshAccessToken(): Observable<void> {
    return this.userService.token().pipe(
      take(1),
      map(m => {
        if (m.isSuccess) {
          this.accessToken.next(m.content?.accessToken ?? '');

          // required for old ajs
          this.localStorageService.set('jwtToken', m.content?.accessToken, {
            serializer: this.passThruSerializer,
          } as ServiceOptions);
        }
      })
    );
  }

  trySignInFromLocalStorage(): Observable<boolean> {
    const token = this.localStorageService.get('jwtToken', {
      serializer: this.passThruSerializer,
    } as ServiceOptions) as string;

    if (token) {
      try {
        jwtDecode(token) as JwtPayload;
        this.accessToken.next(token);
        return of(true);
      } catch (e: unknown) {
        if (e instanceof InvalidTokenError) {
          this.signOut();
          return of(false);
        }
      }
    }

    return of(false);
  }

  impersonateUser(username: string): Observable<TokenResponseResult> {
    return this.userService
      .impersonate({ username: username.trim() } as ImpersonateRequest)
      .pipe(
        take(1),
        tap(result => {
          if (result.content?.accessToken) {
            // clear existing tokens
            this.localStorageService.remove('jwtToken');
            this.localStorageService.remove('authMethod');
            this.cookieService.delete('JSESSIONID', '/');

            // clear to prevent issues with simap when switching between users
            this.cookieService.deleteAll('/');
            window.sessionStorage.clear();

            this.accessToken.next(result.content?.accessToken);

            this.localStorageService.set(
              'jwtToken',
              result.content?.accessToken,
              {
                serializer: this.passThruSerializer,
              } as ServiceOptions
            );

            this.localStorageService.set('authMethod', 0, {
              serializer: this.passThruSerializer,
            } as ServiceOptions);
          }
        })
      );

    // return this.authenticationService
    //     .impersonateUser({
    //         username,
    //     } as CredentialsDto)
    //     .pipe(
    //         take(1),
    //         catchError((errorResponse: HttpErrorResponse) => {
    //             return throwError(errorResponse);
    //         }),
    //         map((m) => {
    //             if (
    //                 m.message.key !==
    //                 MessageDto.KeyEnum.TokenCreatedSuccessfully
    //             ) {
    //                 throw m.message;
    //             } else {
    //                 return m?.token ?? '';
    //             }
    //         }),
    //         tap((token) => this.accessToken.next(token)),
    //         tap((token) => {
    //             this.localStorageService.remove('jwtToken');
    //             this.localStorageService.remove('authMethod');
    //             this.cookieService.delete('JSESSIONID', '/');

    //             this.localStorageService.set('jwtToken', token, {
    //                 serializer: this.passThruSerializer,
    //             } as ServiceOptions);

    //             this.localStorageService.set('authMethod', 0, {
    //                 serializer: this.passThruSerializer,
    //             } as ServiceOptions);

    //             // clear to prevent issues with simap when switching between users
    //             this.cookieService.deleteAll('/');
    //             window.sessionStorage.clear();
    //         }),
    //     );
  }
}

/**
 * used only for set/get of jwtToken in localStorage
 * DefaultStorageSerializer uses Json.stringify leading to tokens escaped with ""
 */
export class PassThruSerializer implements StorageSerializer {
  deserialize<T = unknown>(storedValue: string): T {
    return storedValue as unknown as T;
  }

  public serialize<T = unknown>(value: T): string {
    return value as string;
  }
}
